import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SessionService } from '@core/config/session';
import { environment } from '@environments/environment';
import { Observable, catchError, of, throwError } from 'rxjs';
import { AirportModel, HandlerInfoModel } from '../models/airport.model';
import { AirportBriefingPort } from './airport-briefing';

/** A refusal that means "not this door", rather than "not this airport". */
function isRefusal(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
}

/**
 * The briefing, from whichever endpoint this viewer is entitled to ask.
 *
 * Both endpoints run the same server-side gate, so the shape of the answer is
 * decided there rather than here; this only picks the door. The public one
 * exists because the authenticated one refuses a request with no session
 * before the gate can answer at all.
 */
@Injectable()
export class AirportBriefingHttpAdapter implements AirportBriefingPort {
  private http = inject(HttpClient);
  private session = inject(SessionService);

  /**
   * `hasSession()` can be wrong in the dangerous direction, so the answer is
   * checked against the server rather than trusted.
   *
   * `BrowserCookieService` writes the auth cookie with a ~10-year maxAge while
   * the token inside it expires in hours, so the predicate is really "was
   * there ever a session on this device". A visitor who signed in last week
   * and opens a shared briefing link today has a cookie and no session: the
   * shell renders signed-out (the resolver asks the server and gets null),
   * and this used to send them to the authenticated endpoint anyway and hand
   * them a failure state -- with the ungated public briefing they are entitled
   * to one URL away.
   *
   * The retry only fires on 401/403, so a 404 or a 500 still surfaces as
   * itself. The signed-out path is unchanged: it goes straight to the public
   * endpoint and never touches an authenticated one.
   */
  getAirport(id: string): Observable<AirportModel> {
    const publicUrl = `${environment.API_ROOT}public-airport/${id}/`;
    if (!this.session.hasSession()) {
      return this.http.get<AirportModel>(publicUrl);
    }
    return this.http.get<AirportModel>(`${environment.API_ROOT}airport/${id}`).pipe(
      catchError(error =>
        isRefusal(error) ? this.http.get<AirportModel>(publicUrl) : throwError(() => error),
      ),
    );
  }

  /**
   * Ops & handling is a gated section, so there is nothing here for a viewer
   * without a session -- and asking anyway is actively harmful:
   * `AirportBriefingLoader` forkJoins this with `getAirport`, and a forkJoin
   * fails as a unit. A 401 on handlers would take the whole briefing down
   * instead of locking one section of it.
   *
   * A stale cookie lands here too, and for the same reason it degrades to an
   * empty list rather than failing: the handler endpoint has no public
   * counterpart to fall back to, and one locked section is a better answer
   * than no briefing.
   *
   * Only published handlers: an unpublished one is a draft and must not be
   * offered in the picker or chosen as the default.
   */
  getHandlerList(id: string): Observable<HandlerInfoModel[]> {
    if (!this.session.hasSession()) {
      return of([]);
    }
    return this.http
      .get<HandlerInfoModel[]>(`${environment.API_ROOT}handler-info?airport=${id}&status=PUBLISHED`)
      .pipe(catchError(error => (isRefusal(error) ? of([]) : throwError(() => error))));
  }
}
